/**
 * AnthropicBillingConnectModal — paste-token form for the Anthropic
 * organization admin connector. User pastes an `sk-ant-admin-...` key
 * from console.anthropic.com → Settings → Admin Keys; backend
 * validates against /v1/organizations/cost_report before persisting.
 *
 * See OpenAIBillingConnectModal for the shared shape — only labels,
 * placeholder, help text, and the submit hook differ.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectAnthropicBilling } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function AnthropicBillingConnectModal({ isOpen, onClose, onConnected }) {
  const [adminKey, setAdminKey] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectAnthropicBilling();

  useEffect(() => {
    if (isOpen) {
      setAdminKey('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!adminKey.trim()) {
      setError('Admin key is required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      { adminKey: adminKey.trim() },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Anthropic Admin. Please check your admin key.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect Anthropic Admin"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Anthropic Admin</h2>
        <p className="lark-modal-description">
          Get your admin key from the{' '}
          <a
            href="https://console.anthropic.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--ion-color-primary)' }}
          >
            Anthropic Console
          </a>{' '}
          → Settings → Admin Keys. Teamy reads daily org-wide spend via{' '}
          <code>/v1/organizations/cost_report</code>.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="anthropic-billing-admin-key">Admin Key</label>
          <input
            id="anthropic-billing-admin-key"
            type="password"
            value={adminKey}
            placeholder="sk-ant-admin-..."
            onChange={(e) => { setAdminKey(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !adminKey.trim()}
            aria-label="Submit Anthropic Admin admin key"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel Anthropic Admin connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
